import React from 'react';
import { mapBenefitItems, mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCardDetail/OfferGroupCardDetail.selectors';
import { connect } from 'react-redux';
import { Box, TextLink, Typography } from '@drone-js/core';
import OfferCardDetailModal from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCardDetail/OfferCardDetailModal';
import { killEvent, stripHtmlTags } from 'core/utils/domUtils';
import AgodaMarkdown from 'component/common/AgodaMarkdown/AgodaMarkdown';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesOfferInclusionSeenEventActivitiesFieldsV1,
    ActivitiesOptionCardSeeDetailClickEventActivitiesFieldsV3,
    ActivitiesSeeMoreInclusionButtonClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesOfferInclusionSeenEventActivitiesManifestV1,
    ActivitiesOptionCardSeeDetailClickEventActivitiesManifestV3,
    ActivitiesSeeMoreInclusionButtonClickEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { getFirstAvailableEnrichedOfferOption } from 'component/page/detail/PackageOptions/PackageOptionsUtils';
import { InclusionItem } from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCardDetail/InclusionItem';
import { StringFormatter } from '@agoda/react-handyman';
import { ActivityOfferToken } from 'core/type/Details.types';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';
import { TextType } from '@drone-js/theme';
import { InView } from 'react-intersection-observer';

const MAX_OFFER_DETAIL_ITEMS = 3;

const OfferCardDetailModalContent: React.FC<{ header: string; children: React.ReactNode }> = ({ header, children }) => {
    return (
        <Box display="block">
            <Typography tag="p" variant="body-s-strong" mb="8">
                {header}
            </Typography>
            {children}
        </Box>
    );
};
OfferCardDetailModalContent.displayName = 'OfferCardDetailModalContent';

const ExtraFeeItems: React.FC<{ contentLocale: string; extraFeeContent: Props['extraFeeContent'] }> = ({ contentLocale, extraFeeContent }) => {
    if (!extraFeeContent) return null;

    return (
        <>
            {extraFeeContent.map((content, contentIndex) => (
                <Box key={contentIndex} mb="12">
                    <Typography tag="h5" variant="body-s" mb="4">
                        {content.title}
                    </Typography>
                    <Typography variant="body-s" lang={contentLocale} tag="p">
                        <AgodaMarkdown dataElementName="extra-fee-description" input={content?.description || ''} />
                    </Typography>
                </Box>
            ))}
        </>
    );
};
ExtraFeeItems.displayName = 'ExtraFeeItems';

const DISPLAY_WARNING_TIMEOUT = 60;

const OfferGroupCardDetail: React.FC<Props> = (props) => {
    const {
        cms,
        contentLocale,
        extraFeeSection,
        extraFeeContent,
        index,
        isLoading,
        isSelectedOffer,
        offer,
        possibleOfferOptions,
        activityTokenFromState,
        offerIndex,
        isOfferComparison,
        redemption,
    } = props;
    const [detailModalVisible, setDetailModalVisible] = React.useState(false);
    const seeDetailButtonRef = React.useRef<HTMLAnchorElement>(null);

    if (!props.offer.offerGroupDetailRef) return null;

    const { hasInclusions, hasExclusions, hasExtraFee, modalContent, inclusionsBenefits, exclusionBenefits } = mapBenefitItems(props);

    const offerDescription = stripHtmlTags(modalContent?.description);
    const invalidContent = !hasInclusions && !hasExclusions && !modalContent?.description && !hasExtraFee;
    const inclusionsBenefitsLength = inclusionsBenefits?.length || 0;

    const onInclusionsSeen = (inView: boolean) => {
        if (inView) {
            trackElementEvent<ActivitiesOfferInclusionSeenEventActivitiesFieldsV1>(
                ActivitiesOfferInclusionSeenEventActivitiesManifestV1.ids,
                {
                    action_element_index: index + 1,
                },
                { activity_id: offer.activityId },
            );
        }
    };

    const inclusionItems = (textColor: TextType, displayNumber: number, markdownFormat?: {}, isOnOfferCard?: boolean) =>
        inclusionsBenefits
            ?.slice(0, displayNumber)
            .map((benefit, i) => (
                <InclusionItem
                    key={`inclusion_${i}`}
                    benefit={benefit}
                    isInclusion={true}
                    cms={cms}
                    contentLocale={contentLocale}
                    textColor={textColor}
                    markdownFormat={markdownFormat}
                    isOnOfferCard={isOnOfferCard}
                />
            ));

    const exclusionItems = exclusionBenefits?.map((benefit, i) => (
        <InclusionItem key={`exclusion_${i}`} benefit={benefit} isInclusion={false} cms={cms} contentLocale={contentLocale} textColor="base.default" />
    ));

    const handleSeeDetails = (e: React.MouseEvent) => {
        killEvent(e);
        if (!isOfferComparison) {
            trackElementEvent<ActivitiesOptionCardSeeDetailClickEventActivitiesFieldsV3>(
                ActivitiesOptionCardSeeDetailClickEventActivitiesManifestV3.ids,
                {
                    action_element_index: index + 1,
                },
                { activity_id: offer.activityId },
            );
            if (isSelectedOffer) {
                if (possibleOfferOptions.length === 1 && !invalidContent) {
                    setDetailModalVisible(true);
                    props.setIsDisplayWarning(false);
                } else {
                    setDetailModalVisible(false);
                    props.setIsDisplayWarning(true);
                }
            } else if (offer.enrichedOfferOption?.length === 1) {
                setDetailModalVisible(true);
            } else {
                props.setOffer({ offer, offerIndex });

                const enrichedOfferOption = offer.enrichedOfferOption || [];
                const firstAvailableOfferOption = getFirstAvailableEnrichedOfferOption(enrichedOfferOption);
                const bookingRequirementId = firstAvailableOfferOption?.bookingRequirementRefId ?? enrichedOfferOption[0]?.bookingRequirementRefId ?? -1;
                const offerToken: ActivityOfferToken = enrichedOfferOption[0]?.offerOptionToken
                    ? { activityToken: enrichedOfferOption[0].offerOptionToken, enableOfferFilter: true }
                    : { activityToken: activityTokenFromState, enableOfferFilter: false };
                props.setBookingRequirementById(bookingRequirementId);
                props.onSelectOffer(offerToken, `#${props.navigationId}`);

                setDetailModalVisible(false);
                setTimeout(() => {
                    props.setIsDisplayWarning(true);
                }, DISPLAY_WARNING_TIMEOUT);
            }
        } else {
            trackElementEvent<ActivitiesSeeMoreInclusionButtonClickEventActivitiesFieldsV1>(
                ActivitiesSeeMoreInclusionButtonClickEventActivitiesManifestV1.ids,
                {
                    action_element_index: index + 1,
                },
                { activity_id: offer.activityId },
            );
            setDetailModalVisible(true);
        }
    };

    const setModalClose = () => {
        setDetailModalVisible(false);
    };

    const ariaLabel = props.offerGroupDetail?.offerGroupTitle ? StringFormatter.format(cms[321441], cms[126897], props.offerGroupDetail?.offerGroupTitle) : undefined;

    const markdownFormat = {
        img: { props: { style: { display: 'none' } } },
    };

    return (
        <div>
            <Box display="flex" justifyContent="space-between" flexDirection="column" mb={isSelectedOffer ? undefined : '24'}>
                <Box display="flex" justifyContent="space-between">
                    <Typography tag="h3" variant="heading-default" textColor="base.default" me="16" lang={contentLocale}>
                        {props.offerGroupDetail?.offerGroupTitle}
                    </Typography>
                    {!(props.isHideSeeDetailsButtonForNoContent && invalidContent) && (
                        <Box minWidth="20%" textAlign="end">
                            <TextLink
                                role="button"
                                hierarchy="primary"
                                label={cms[126897]}
                                aria-label={ariaLabel}
                                textVariant="body-s"
                                trailingIcon="fill.arrow.thin-forward"
                                onClick={handleSeeDetails}
                                data-element-name="activities-option-card-see-detail"
                                data-testid="activities-option-card-see-detail"
                                data-element-index={index + 1}
                                ref={seeDetailButtonRef}
                            />
                        </Box>
                    )}
                </Box>
                <Feature.On name={FeatureName.OfferComparison}>
                    {inclusionItems && (
                        <InView onChange={onInclusionsSeen} triggerOnce={true} threshold={1}>
                            <Box display="block" data-testid="activities-option-detail" data-element-name="activities-offer-inclusion">
                                {inclusionItems('feedback.positive', MAX_OFFER_DETAIL_ITEMS, markdownFormat, true)}
                            </Box>
                        </InView>
                    )}
                    {inclusionsBenefitsLength > MAX_OFFER_DETAIL_ITEMS && (
                        <div>
                            <TextLink
                                role="button"
                                hierarchy="primary"
                                label={StringFormatter.format(cms[366068], inclusionsBenefitsLength - MAX_OFFER_DETAIL_ITEMS)}
                                aria-label={StringFormatter.format(cms[366068], inclusionsBenefitsLength - MAX_OFFER_DETAIL_ITEMS)}
                                textVariant="caption-default-strong"
                                onClick={handleSeeDetails}
                                data-element-name="activities-see-more-inclusion-button"
                                data-testid="activities-option-detail-card-see-more"
                                data-element-index={index + 1}
                                ref={seeDetailButtonRef}
                            />
                        </div>
                    )}
                </Feature.On>
            </Box>
            {!invalidContent && (
                <OfferCardDetailModal modalVisible={detailModalVisible && !isLoading} onCloseModal={setModalClose}>
                    {hasInclusions && <OfferCardDetailModalContent header={cms[126850]}>{inclusionItems('base.default', inclusionsBenefitsLength)}</OfferCardDetailModalContent>}
                    {hasExclusions && <OfferCardDetailModalContent header={cms[288291]}>{exclusionItems}</OfferCardDetailModalContent>}
                    {extraFeeSection && (
                        <OfferCardDetailModalContent header={extraFeeSection.title || cms[244893]}>
                            <ExtraFeeItems extraFeeContent={extraFeeContent} contentLocale={contentLocale} />
                        </OfferCardDetailModalContent>
                    )}
                    {offerDescription && (
                        <OfferCardDetailModalContent header={cms[288252]}>
                            <Typography variant="body-s" tag="p" key="OfferCardDetailModalContent">
                                {offerDescription}
                            </Typography>
                        </OfferCardDetailModalContent>
                    )}
                    <Feature.On name={FeatureName.AddRedemptionInstructionOnDetails}>
                        {redemption && (
                            <OfferCardDetailModalContent header={cms[423847]}>
                                <Typography variant="body-s" tag="p" data-testid="redemption-instructions">
                                    {redemption.specialInstructions}
                                </Typography>
                            </OfferCardDetailModalContent>
                        )}
                    </Feature.On>
                </OfferCardDetailModal>
            )}
        </div>
    );
};

OfferGroupCardDetail.displayName = 'OfferGroupCardDetail';
export default connect(mapStateToProps, mapDispatchToProps)(OfferGroupCardDetail);
